/**
 * vendorSlice.js
 *
 * This Redux slice manages the vendor/store data for the dashboard.
 * It uses createAsyncThunk to fetch the vendor's profile from the API asynchronously.
 *
 * Key Responsibilities:
 * - Load the vendor data when the dashboard mounts
 * - Manage loading, success, and error states for vendor data fetching
 * - Store the fetched vendor object in Redux state for global access
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

// Asynchronous thunk action to fetch vendor details from the backend
export const fetchVendor = createAsyncThunk("vendor/fetchVendor", async () => {
  const response = await api.get("/vendor/store/me");
  // Return only the vendor object from the response
  return response.data.vendor;
});

// Slice definition
const vendorSlice = createSlice({
  name: "vendor", // Redux state key: state.vendor
  initialState: {
    data: null, // Holds the vendor data once fetched
    loading: false, // Flag to track if fetch is in progress
    error: null, // Stores error message (if any) on failed fetch
  },
  reducers: {
    // No manual reducers needed currently; all handled by extraReducers
  },
  extraReducers: (builder) => {
    builder
      // When fetchVendor is pending (loading)
      .addCase(fetchVendor.pending, (state) => {
        state.loading = true;
        state.error = null; // Clear previous errors
      })

      // When fetchVendor is fulfilled (success)
      .addCase(fetchVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload; // Store the fetched vendor
      })

      // When fetchVendor is rejected (error)
      .addCase(fetchVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message; // Store the error message
      });
  },
});

// Export the reducer to include in Redux store
export default vendorSlice.reducer;
